import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { LastMonthRevenueEntity } from "../../domain/entities/LastMonthRevenueEntity";
import type { LastMonthRevenueCardProps } from "../components/revenue_components/LastMonthRevenueCard";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapError,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";
import { useRevenueBootstrap } from "./useRevenueBootstrap";

const BAR_MAX_HEIGHT = 80;

function truncateToDecimals(value: number, decimals: number): number {
  if (!Number.isFinite(value) || decimals < 0) return value;
  const factor = 10 ** decimals;
  return Math.trunc(value * factor) / factor;
}

function formatSignedPercentTruncated(value: number, decimals: number): string {
  const truncated = truncateToDecimals(value, decimals);
  const sign = truncated > 0 ? "+" : "";
  return `${sign}${truncated.toFixed(decimals)}%`;
}

function entityToCardProps(
  entity: LastMonthRevenueEntity,
): Omit<LastMonthRevenueCardProps, "loading"> {
  const statusLabel =
    entity.status === "strong"
      ? "Strong"
      : entity.status === "low"
        ? "Low"
        : "Medium";

  const barRevenues = entity.barMonths.map((m) => m.revenue);
  const maxBarRevenue = Math.max(...barRevenues, 1);
  const barHeights = barRevenues.map((r) =>
    Math.max(12, (r / maxBarRevenue) * BAR_MAX_HEIGHT),
  );
  const barLabels = entity.barMonths.map((m) => m.label);
  const barAmounts = entity.barMonths.map((m) => formatPlAmount(m.revenue));

  const momPercent =
    entity.momPercent != null
      ? `${formatSignedPercentTruncated(entity.momPercent, 2)} from previous closed month`
      : "— from previous closed month";

  const ebitdaPercent =
    entity.ebitdaMargin != null
      ? `${truncateToDecimals(entity.ebitdaMargin, 2).toFixed(2)}% EBITDA`
      : "— EBITDA";

  return {
    monthLabel: entity.monthLabel,
    statusLabel,
    revenueAmount: formatPlAmount(entity.revenue),
    momPercent,
    ebitdaPercent,
    barHeights,
    barLabels,
    barAmounts,
  };
}

export function useLastMonthRevenue(): LastMonthRevenueCardProps & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useRevenueBootstrap();

  const [card, setCard] = useState<LastMonthRevenueCardProps>({
    monthLabel: "—",
    statusLabel: "—",
    revenueAmount: "—",
    momPercent: "— from previous closed month",
    ebitdaPercent: "— EBITDA",
    barHeights: [],
    barLabels: [],
    barAmounts: [],
    loading: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getRevenueBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Revenue bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.lastMonth;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Last month revenue unavailable");
      }

      setCard({ ...entityToCardProps(section.data), loading: false });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load last month revenue";
      setError(message);
      setCard({
        monthLabel: "—",
        statusLabel: "—",
        revenueAmount: "—",
        momPercent: "— from previous closed month",
        ebitdaPercent: "— EBITDA",
        barHeights: [],
        barLabels: [],
        barAmounts: [],
        loading: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ...card,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
